import React from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Prompt } from 'react-router-dom';
import { isEqual } from 'lodash';
import { faPlusCircle, faSave, faTruck } from '@fortawesome/free-solid-svg-icons';
import { Button, Col, FormGroup, Spinner } from 'reactstrap';
import 'react-datepicker/dist/react-datepicker.css';
import { v4 as uuid } from 'uuid';
import {
    AppPageForm,
    GroupedRow,
    FormLabel,
    SubHeading,
    FormGroupColumn,
    SmallButton,
    FlexCenterRow,
    FormCircularProgress,
    toasty,
    onReactSelectChanged,
    onFieldChange,
    CollapseUnderlineHeader,
} from '../common/forms/FormElements';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { util } from '../Util';
import CommonContext, { ApiRoutes, AppNavPaths, ContactTypes } from '../Common';
import OrganizationContactForm from '../organization/OrganizationContactForm';
import JobScheduler from './JobScheduler';
import JobForm from './JobForm';
import AddressFormNew from '../address/AddressFormNew';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BaseFormViewModel } from '../common/ViewModel';
import LocationSelector from './LocationSelector';
import { OrganizationContact } from '../organization/Organization';
import { Project } from './Project';
import { EquipmentOnSite } from './EquipmentOnSite';
import Input from 'components/common/forms/Input';
import { Textarea } from 'components/common/forms/Textarea';

class ProjectForm extends React.Component {
    static contextType = CommonContext;

    constructor(props) {
        super(props);

        this.state = {
            selectedDate: null,
            contract: null,
            contactsOptions: null,
            dispatchLocations: [],
            contractResources: [],
            customerSubcontractors: [],
            slideOpen: false,
            selectedAddressType: null,
            usCounties: [],
            selectedContact: null,
            selectedContactIdProp: null,
            showContactsForm: false,
            allowContactTypeEdit: true,
            contactFormIsReadOnly: false,
            meetingLocationRequired: {},
            jobLocationRequired: {},
            selectedJob: null,
            equipmentOnSiteOpen: true,
            project: new Project(),
            jobTypesOptions: null,
            originalProject: util.object.clone(new Project()),
            ...new BaseFormViewModel(),
        };

        // Refs
        this.formRef = React.createRef();
        this.addressFormRef = React.createRef();
        this.contactFormRef = React.createRef();

        // Bind any methods if not using arrow functions.
        this.onSubmit = this.onSubmit.bind(this);
        this.onChange = this.onChange.bind(this);
        this.onSelectChanged = this.onSelectChanged.bind(this);
        this.onOrganizationContactSaved = this.onOrganizationContactSaved.bind(this);
    }

    onSelectChanged = onReactSelectChanged;
    onChange = onFieldChange;

    onClearErrors = () => {
        this.setState({ errors: {} });
    };

    refreshProject = async () => {
        await this.populateState();
    };

    resetForm = () => {
        this.setState({
            isValidated: false,
            errors: {},
            saving: false,
            project: util.object.clone(this.state.originalProject)
        });
    };

    componentDidMount() {
        const { id, contractId } = this.props.match.params;

        if (id || contractId) {
            this.populateState();
        }
    }

    componentDidUpdate(prevProps) {
        const prevId = prevProps.match.params.projectId || prevProps.match.params.id;
        const nextId = this.props.match.params.projectId  || this.props.match.params.id;
        if (nextId && nextId !== prevId) {
            this.populateState();
        }
    }

    async populateState() {
        this.setState({ loading: true });

        try {
            // Get parameters from the URL
            const { id, contractId } = this.props.match.params;
            let projectData = null;
            let projectState = { ...this.state.project };
            let contractData = null;

            if (id) {
                projectData = await util.fetch.js(ApiRoutes.project.byId(id));
                if (projectData) {
                    projectState = projectData;
                }
            }

            const contractIdToFetch = contractId || (projectData && projectData.contractId);

            if (contractIdToFetch) {
                if (id && projectData) {
                    contractData = projectData.contract;
                } else {
                    contractData = await util.fetch.js(ApiRoutes.contract.byId(contractIdToFetch));
                }

                if (contractData) {
                    projectState = {
                        ...projectState,
                        contractId: contractData.id,
                        contractNumber: contractData.number,
                        contractDescription: contractData.description,
                        contractCustomer: contractData.customerName,
                    };
                }
            }

            const contractResources = contractIdToFetch ? await util.fetch.js(ApiRoutes.job.getResources(contractIdToFetch)) : [];

            // Fetch contacts, subcontractors, dispatch locations, and counties if a company exists.
            if (contractData?.companyId) {
                let [contactsData, subcontractors, dispatchLocations, counties, jobTypes] = await Promise.all([
                    this.getCompanyContactsOptions(
                        contractData.companyId,
                        projectData?.requestedById,
                        projectData?.foremanId,
                        projectData?.projectManagerId
                    ),
                    util.fetch.js(ApiRoutes.typeAheads.subcontractors(contractData.companyId)),
                    util.fetch.js(ApiRoutes.typeAheads.dispatchLocations()),
                    util.fetch.js(ApiRoutes.USCounties.all()),
                    util.fetch.js(ApiRoutes.typeAheads.jobTypes()),
                ]);

                // If this is a new project (projectData is null), then assign contract details to the project.
                if (!projectData) {
                    // Filter dispatch locations based on those allowed by the contract
                    dispatchLocations = dispatchLocations.filter((x) => (contractData.dispatchLocations ?? []).includes(x.value));

                    // If only one dispatch location exists, set it automatically
                    if (dispatchLocations.length === 1) {
                        projectState = { ...projectState, dispatchCompanyId: dispatchLocations[0].value };
                    }

                    projectState = {
                        ...projectState,
                        contractId: contractData.id,
                        contractNumber: contractData.number,
                        contractDescription: contractData.description,
                        contractCustomer: contractData.customerName,
                        description: contractData.description,
                    };
                }

                this.setState({
                    contractResources,
                    project: projectState,
                    originalProject: util.object.clone(projectState),
                    contract: contractData,
                    dispatchLocations: dispatchLocations || [],
                    contactsOptions: contactsData || {},
                    customerSubcontractors: subcontractors || [],
                    usCounties: counties || [],
                    jobTypesOptions: jobTypes || [],
                });

                // Check for an openJob query parameter and auto-open the job modal if found.
                const params = new URLSearchParams(this.props.location.search);
                const openJobId = params.get('openJob');

                if (openJobId && projectState.jobs) {
                    const jobToOpen = projectState.jobs.find(
                        (job) => job.id && job.id.toString() === openJobId
                    );

                    if (jobToOpen) {
                        // Use jobDate if available, otherwise default to today
                        const selectedDate = jobToOpen.jobDate && new Date(jobToOpen.jobDate);
                        this.setState({ selectedJob: jobToOpen, selectedDate, slideOpen: true });
                    }
                }
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            this.setState({ errors: error.message });
        } finally {
            this.setState({ loading: false });
        }
    }

    getWeekStartDate = (date) => {
        const d = new Date(date);
        const day = d.getDay();
        // Compute the difference to Monday (if Sunday, use 6)
        const diff = d.getDate() - (day === 0 ? 6 : day - 1);
        const startOfWeek = new Date(d.setDate(diff));
        return startOfWeek.toISOString().split('T')[0];
    };

    handleJobCreate = (date) => {
        const { project } = this.state;

        this.setState({ selectedDate: date, slideOpen: true });

        if (this.context.setFormOpened) {
            this.context.setFormOpened(true);
        }

        const weekStartDate = this.getWeekStartDate(date);

        const selectedJob = {
            weekOfDate: new Date(weekStartDate).toISOString(),
            foremenId: project.foremanId,
            requestedById: project.requestedById,
            projectManagerId: project.projectManagerId,
            subcontractorCompanyId: project.subcontractorId,
            meetingLocation: project.meetingLocation,
            jobLocation: project.jobLocation,
        };

        this.setState({ selectedJob });
    };

    handleJobSelect = (job, clickedDate) => {
        if (!clickedDate) {
            console.error('Clicked date is not provided!');
            return;
        }

        const selectedDate = job.jobDate ? new Date(job.jobDate) : clickedDate;
        this.setState({
            selectedDate,
            selectedJob: job,
            slideOpen: true,
        });
    };

    handleJobSave = (savedJob) => {
        const { project } = this.state;

        // Ensure jobs array exists
        let updatedJobs = (project?.jobs ?? []).map(job =>
            (job.id && job.id === savedJob.id) ||
                (job.tempSessionId && job.tempSessionId === savedJob.tempSessionId)
                ? { ...job, ...savedJob, weekOfDate: job.weekOfDate } // Preserve weekOfDate
                : job
        );

        // If job wasn't found, add it
        if (!(project?.jobs ?? []).some(job =>
            (job.id && job.id === savedJob.id) ||
            (job.tempSessionId && job.tempSessionId === savedJob.tempSessionId)
        )) {
            updatedJobs = [...updatedJobs, savedJob];
        }

        // Update state
        this.setState({
            project: {
                ...project,
                jobs: updatedJobs,
            },
        });

        toasty.success(`Job ${savedJob.isNew ? 'added' : 'updated'} successfully!`);
    };

    onSaveAddress = (address) => {
        const { selectedAddressType, usCounties, project } = this.state;

        if (!selectedAddressType) return;

        const countyData = util.getCounty(usCounties, address);

        const updatedProject = {
            ...project,
            [selectedAddressType]: { ...address, county: countyData },
        };

        if (address.duplicate) {
            if (selectedAddressType === 'jobLocation' && !updatedProject.meetingLocation) {
                updatedProject.meetingLocation = { ...address, id: uuid(), county: countyData };
            }
            if (selectedAddressType === 'meetingLocation' && !updatedProject.jobLocation) {
                updatedProject.jobLocation = { ...address, id: uuid(), county: countyData };
            }
        }

        this.setState({ project: updatedProject, selectedAddressType: null });
    };

    onSubmit = async (e) => {
        if (!this.validateProject()) return false;
        if (this.state.saving) return false;

        const { project } = this.state;
        this.setState({ saving: true });

        try {
            let projectToSubmit = { ...project };

            // Clean up location objects before sending
            projectToSubmit.meetingLocation = typeof projectToSubmit.meetingLocation?.id === 'string'
                ? { ...projectToSubmit.meetingLocation, id: null }
                : projectToSubmit.meetingLocation;

            projectToSubmit.jobLocation = typeof projectToSubmit.jobLocation?.id === 'string'
                ? { ...projectToSubmit.jobLocation, id: null }
                : projectToSubmit.jobLocation;

            // Exclude jobs from comparison
            const projectWithoutJobs = { ...projectToSubmit };
            delete projectWithoutJobs.jobs;

            const originalProjectWithoutJobs = { ...this.state.originalProject };
            delete originalProjectWithoutJobs.jobs;

            const isProjectModified = !isEqual(projectWithoutJobs, originalProjectWithoutJobs);

            let projectId = project.id;

            if (!project.id || isProjectModified) {
                const apiRoute = project.id
                    ? ApiRoutes.project.update(project.id)
                    : ApiRoutes.project.create();

                const response = project.id
                    ? await util.fetch.put(apiRoute, projectWithoutJobs, util.fetch.format.none)
                    : await util.fetch.post(apiRoute, projectWithoutJobs, util.fetch.format.none);

                if (!response.ok) throw new Error('Failed to save project');
                const savedProjectData = await response.json();
                projectId = project.id ? project.id : savedProjectData?.data;
                projectToSubmit = {
                    ...projectToSubmit,
                    id: projectId // Ensure the project ID is updated
                };
            }

            const jobsToProcess = (project.jobs || []).filter(
                (job) => job.isNew || job.isUpdated
            );

            // Process jobs sequentially
            let updatedJobs = [...(project.jobs || [])];

            for (const job of jobsToProcess) {
                let jobToSubmit = { ...job, projectId };

                if (job.isNew || !job.id) {
                    const response = await util.fetch.post(
                        ApiRoutes.job.create(),
                        jobToSubmit,
                        util.fetch.format.none
                    );

                    if (!response.ok) {
                        throw new Error(`Failed to create job: ${jobToSubmit.description}`);
                    }

                    const createdJobId = await response.json();

                    updatedJobs = updatedJobs.map((j) => ({
                        ...j,
                        id: j.tempSessionId === job.tempSessionId ? createdJobId : j.id,
                        isNew: false,
                    }));
                } else {
                    const updateResponse = await util.fetch.put(
                        ApiRoutes.job.update(job.id),
                        jobToSubmit,
                        util.fetch.format.none
                    );

                    if (!updateResponse.ok) {
                        throw new Error(`Failed to update job: ${jobToSubmit.description}`);
                    }

                    updatedJobs = updatedJobs.map((j) => ({
                        ...j,
                        isUpdated: false,
                    }));
                }
            }

            toasty.success(
                `Project ${project.id ? 'updated' : 'created'} successfully!`
            );

            // Update project with new job data
            const updatedProject = { ...projectToSubmit, jobs: updatedJobs };

            this.setState({
                project: updatedProject,
                originalProject: util.object.clone(updatedProject)
            }, () => {
                this.resetForm();

                if (!project.id) {
                    //route to the newly created project's page using its ID.
                    this.props.history.push(`/project/${projectId}`);
                }
            });


        } catch (error) {
            console.error('Error saving project:', error);
            this.resetForm();
            this.setState({ errors: error?.message || 'An error occurred while saving.' });
        } finally {
            this.setState({ saving: false });
        }
    };

    validateProject = () => {
        let valid = true;
        const errors = {};
        const requiredStyle = {
            border: '1px solid',
            borderRadius: '0.25rem',
            borderColor: '#ed1c24',
        };
        const { project, contract } = this.state;

        // Check meeting location
        if (!project.meetingLocation) {
            valid = false;
            errors.meetingLocation = 'Meeting location is required.';
            this.setState({ meetingLocationRequired: requiredStyle });
        } else {
            this.setState({ meetingLocationRequired: {} });
        }

        // Check job location
        if (!project.jobLocation) {
            valid = false;
            errors.jobLocation = 'Job location is required.';
            this.setState({ jobLocationRequired: requiredStyle });
        } else {
            this.setState({ jobLocationRequired: {} });
        }

        // Check foreman
        if (!project.requestedById) {
            valid = false;
            errors.requestedBy = 'A requested by selection is required.';
        }

        // Check foreman
        if (!project.foremanId) {
            valid = false;
            errors.foreman = 'A foreman selection is required.';
        }

        // Check Dispatching
        if (!project.dispatchCompanyId) {
            valid = false;
            errors.dispatchCompany = 'A dispatch selection is required.';
        }

        // Check project name if required by the contract/tenant setting
        if ((contract ?? {}).requireProjectInformation && !project.projectName) {
            valid = false;
            errors.projectName = 'Project Name is required.';
        }

        // Check project name if required by the contract/tenant setting
        if ((contract ?? {}).requireProjectInformation && !project.projectManagerId) {
            valid = false;
            errors.projectManager = 'Project Manager is required.';
        }

        // Check subcontractor if subcontractorsApply is true
        if ((contract ?? {}).subcontractorsApply && !project.subcontractorId) {
            valid = false;
            errors.subcontractorId = 'A subcontractor selection is required.';
        }

        // Optionally show a general message if not valid
        if (!valid) {
            toasty.error('Please fill in all the required details.');
        }

        this.setState({ isValidated: valid, errors: errors, saving: valid });

        return valid;
    };

    // ----- Contact Selector Methods (Integrated) -----
    async getCompanyContactsOptions(companyId, requestedById, foremanId, projectManagerId) {
        const companyContacts = await util.fetch.js(ApiRoutes.company.contacts(companyId));
        const filteredCTDescriptions = OrganizationContactForm.filteredContactTypes;
        const projectManagerFiltered = [];
        const contactsFiltered = companyContacts.filter((c) => {
            if (!c.isActive) return false;

            const include =
                c.id === requestedById ||
                c.id === foremanId ||
                c.id === projectManagerId ||
                (c.locationContactTypes &&
                    c.locationContactTypes.some((t) => filteredCTDescriptions.some((d) => t.description === d)));

            if (include && c.locationContactTypes && c.locationContactTypes.some((t) => t.id === ContactTypes.ProjectManager)) {
                projectManagerFiltered.push(c);
            }

            return include;
        });

        return {
            companyContactsOptions: contactsFiltered.map((x) => ({ label: x.contactName, value: x.id })),
            projectManagerContactOptions: projectManagerFiltered.map((x) => ({ label: x.contactName, value: x.id })),
        };
    }

    onAddContact = (prop) => {
        const { contract } = this.state;
        const { setFormOpened } = this.context;

        if (!contract) return;

        if (setFormOpened) setFormOpened(true);

        if (this.contactFormRef.current) {
            this.contactFormRef.current.resetForm();
        }
        const newContact = new OrganizationContact();
        newContact.id = null;
        newContact.companyId = contract.companyId;

        this.setState({
            selectedContact: newContact,
            selectedContactIdProp: prop,
            showContactsForm: true,
            allowContactTypeEdit: true,
        });
    };

    onEditContact = async (prop) => {
        const { project } = this.state;

        if (!project[prop]) return;

        try {
            const selectedContactData = await util.fetch.js(ApiRoutes.locationContact.byId(project[prop]));
            if (this.contactFormRef.current) {
                this.contactFormRef.current.resetForm();
            }
            this.setState({
                selectedContact: selectedContactData,
                selectedContactIdProp: prop,
                showContactsForm: true,
                allowContactTypeEdit: false,
            });
        } catch (error) {
            console.error('Error fetching contact:', error);
        }
    };

    onOrganizationContactSaved = async () => {
        const { selectedContact, selectedContactIdProp, contract } = this.state;

        if (!selectedContactIdProp || !selectedContact) return;

        this.setState({ contactFormIsReadOnly: true });
        const isNew = !selectedContact.id;

        try {
            const result = await util.fetch.andGetResponse(
                isNew ? util.fetch.types.post : util.fetch.types.put,
                isNew ? ApiRoutes.locationContact.create() : ApiRoutes.locationContact.update(selectedContact.id),
                selectedContact
            );
            if (result) {
                toasty.success('Contact saved successfully.');

                const updatedProject = { ...this.state.project, [selectedContactIdProp]: result.id ?? result };
                this.setState({ project: updatedProject });

                const companyContacts = await this.getCompanyContactsOptions(contract.companyId);
                this.setState({ contactsOptions: companyContacts });
                this.setState({ showContactsForm: false });
            }
        } catch (error) {
            console.error('Error saving contact:', error);
        } finally {
            this.setState({ contactFormIsReadOnly: false });
        }
    };

    getDispatchPath(dispatchOfficeLocationId) {
        const user = this.context.user;
        let path = `${AppNavPaths.Dispatch}`;
    
        if (
            user.isAdmin ||
            (
                Array.isArray(user.expandedDispatchCompanies) &&
                user.expandedDispatchCompanies.includes(dispatchOfficeLocationId)
            )
        ) {
            path += `?dispatchOfficeId=${dispatchOfficeLocationId}`;
        }
    
        return path;
    }

    render() {
        const {
            selectedDate,
            contract,
            contractResources,
            contactsOptions,
            dispatchLocations,
            customerSubcontractors,
            slideOpen,
            project,
            meetingLocationRequired,
            jobLocationRequired,
            selectedJob,
            usCounties,
            showContactsForm,
            contactFormIsReadOnly,
            allowContactTypeEdit,
            selectedContact,
            originalProject,
            saving,
            isValidated,
            errors,
            loading,
            jobTypesOptions,
        } = this.state;

        const tenantSettings = this.context.tenant.tenantSettings;

        const existing = !!project.id;

        const projectWithoutJobs = { ...project };
        delete projectWithoutJobs.jobs;

        const originalProjectWithoutJobs = { ...originalProject };
        delete originalProjectWithoutJobs.jobs;

        // Check if any job is new or updated
        const hasUnsavedJobs = this.state.project.jobs?.some(job => job.isNew || job.isUpdated);

        const showConfirmationPrompt =
            !saving &&
            (!isEqual(originalProjectWithoutJobs, projectWithoutJobs) || hasUnsavedJobs);

        const dispatchPath = this.getDispatchPath(project?.dispatchCompanyId);

        if (loading) {
            return <FormCircularProgress />;
        }

        return (
            <>
                <Prompt
                    when={showConfirmationPrompt}
                    message="You have unsaved changes, are you sure you want to leave?"
                />
                <AppPageForm
                    backUrl={`${AppNavPaths.Contract}/${project.contractId}`}
                    backLabel="Go to Contract"
                    formId="projectForm"
                    formHeadingIcon={faTruck}
                    formHeading={
                        !existing
                            ? 'New Project'
                            : `Edit Project: ${(project ?? {}).contractNumber}${project.contractSequence ? `-${project.contractSequence}` : ''
                            }`
                    }
                    formName="projectForm"
                    formRef={this.formRef}
                    onSubmit={this.onSubmit}
                    setIsValidated={(value) => {
                        this.setState({ isValidated: value });
                    }}
                    isValidated={isValidated}
                    saving={saving}
                    errors={errors}
                    loading={loading}
                    onClearErrors={this.onClearErrors}
                >
                    <SubHeading>Contract Details</SubHeading>
                    <GroupedRow>
                        <FormGroupColumn>
                            <Input
                                id="contractNumber"
                                name="project.contractNumber"
                                label="Contract Number"
                                value={(contract ?? {}).number}
                                readOnly
                                className="form-control-plaintext no-readonly-bg"
                            />
                            <Input
                                id="contractDescription"
                                name="project.contractDescription"
                                label="Description"
                                value={(contract ?? {}).description}
                                readOnly
                                className="form-control-plaintext no-readonly-bg"
                            />
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <Input
                                id="customerName"
                                name="project.customerName"
                                label="Customer Name"
                                value={(contract ?? {}).customerName}
                                readOnly
                                className="form-control-plaintext no-readonly-bg"
                            />
                            {(contract ?? {}).operationsCenterName && (
                                <Input
                                    id="operationsCenterName"
                                    name="project.operationsCenterName"
                                    label="Operations Center"
                                    value={(contract ?? {}).operationsCenterName}
                                    readOnly
                                    className="form-control-plaintext no-readonly-bg"
                                />
                            )}
                            {((contract ?? {}).isUnionWork === true || (contract ?? {}).companyUsesContractSchedule === true) && (
                                <Input
                                    id="schedule"
                                    name="project.schedule"
                                    label="Schedule"
                                    value={(contract ?? {}).contractScheduleTypeName}
                                    readOnly
                                    className="form-control-plaintext no-readonly-bg"
                                />
                            )}
                        </FormGroupColumn>
                    </GroupedRow>

                    <SubHeading>Setup</SubHeading>
                    <GroupedRow>
                        <FormGroupColumn>
                            <FormGroup className="relative">
                                <FormLabel htmlFor="requestedBy" text="Requested By" required />
                                <SmallButton
                                    color="outline-primary"
                                    className="floating ml-auto mr-0"
                                    onClick={() => this.onAddContact('requestedById')}
                                >
                                    <FontAwesomeIcon
                                        icon={faPlusCircle}
                                        size="md"
                                        className="mr-2"
                                    />
                                    Add Contact
                                </SmallButton>
                                <ValidatedSelect
                                    id="requestedBy"
                                    name="project.requestedById"
                                    options={contactsOptions?.companyContactsOptions}
                                    value={
                                        (contactsOptions?.companyContactsOptions ?? []).find(
                                            (x) => x.value === project.requestedById
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    required
                                    validationMessage="A requested by selection is required."
                                />
                                {!!project?.requestedById && (
                                    <SmallButton
                                        color="link"
                                        className="site-link w-auto text-right"
                                        onClick={() => this.onEditContact('requestedById')}
                                    >
                                        Edit Requestor Details
                                    </SmallButton>
                                )}
                            </FormGroup>
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <FormGroup>
                                <FormLabel
                                    htmlFor="project.dispatchCompanyId"
                                    text="Dispatching"
                                    required
                                />
                                <ValidatedSelect
                                    id="project.dispatchCompanyId"
                                    name="project.dispatchCompanyId"
                                    options={dispatchLocations}
                                    value={
                                        (dispatchLocations ?? []).find(
                                            (x) => x.value === project.dispatchCompanyId
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    required
                                    validationMessage="A dispatch selection is required."
                                />
                            </FormGroup>
                        </FormGroupColumn>
                    </GroupedRow>
                    <GroupedRow>
                        <FormGroupColumn>
                            <FormGroup className="relative">
                                <>
                                    <FormLabel htmlFor="foremanId" text="Foreman" required />
                                    <SmallButton
                                        color="outline-primary"
                                        className="floating ml-auto mr-0"
                                        onClick={() => this.onAddContact('foremanId')}
                                    >
                                        <FontAwesomeIcon
                                            icon={faPlusCircle}
                                            size="md"
                                            className="mr-2"
                                        />
                                        Add Contact
                                    </SmallButton>
                                </>
                                <ValidatedSelect
                                    id="foremen"
                                    name="project.foremanId"
                                    options={contactsOptions?.companyContactsOptions}
                                    value={
                                        (contactsOptions?.companyContactsOptions ?? []).find(
                                            (x) => x.value === project.foremanId
                                        ) ?? ''
                                    }
                                    onChange={this.onSelectChanged}
                                    required
                                    validationMessage="A foreman selection is required."
                                />
                                {!!project?.foremanId && (
                                    <SmallButton
                                        color="link"
                                        className="site-link w-auto text-right"
                                        onClick={() => this.onEditContact('foremanId')}
                                    >
                                        Edit Foreman Details
                                    </SmallButton>
                                )}
                            </FormGroup>
                        </FormGroupColumn>
                        {contract?.workOrderTypeId !== 3 && (
                            <FormGroupColumn>
                                <Input
                                    name="project.workOrderNumber"
                                    label="Work Order #"
                                    maxLength={44}
                                    value={project.workOrderNumber ?? ''}
                                    onChange={this.onChange}
                                />
                            </FormGroupColumn>
                        )}
                    </GroupedRow>
                    {(tenantSettings?.contractsCanEnforceProjectManagers && (contract ?? {}).requireProjectInformation) && (
                        <>
                            <GroupedRow>
                                <FormGroupColumn>
                                    <Input
                                        name="project.projectName"
                                        label="Project Name"
                                        value={project.projectName ?? ''}
                                        onChange={this.onChange}
                                        required
                                        error="Project Name is required."
                                    />
                                </FormGroupColumn>
                                <FormGroupColumn>
                                    <FormGroup className="relative">
                                        <div className="d-flex flex-row flex-nowrap align-items-baseline justify-content-start">
                                            <FormLabel
                                                htmlFor="projectManager"
                                                text="Project Manager"
                                                required
                                            />
                                            <SmallButton
                                                color="outline-primary"
                                                className="floating ml-auto mr-0"
                                                onClick={() => this.onAddContact(
                                                    'projectManagerId',
                                                )}
                                            >
                                                <i className="fa fa-plus-circle fa-md mr-2" />
                                                Add Contact
                                            </SmallButton>
                                        </div>
                                        <ValidatedSelect
                                            id="projectManager"
                                            name="project.projectManagerId"
                                            options={contactsOptions?.projectManagerContactOptions || []}
                                            value={
                                                (contactsOptions?.projectManagerContactOptions || []).find(
                                                    (x) => x.value === project?.projectManagerId
                                                ) || ''}
                                            onChange={this.onSelectChanged}
                                            required
                                            validationMessage="A Project Manager selection is required."
                                        />
                                        {!!project?.projectManagerId && (
                                            <SmallButton
                                                color="link"
                                                className="site-link w-auto text-right"
                                                onClick={() => this.onEditContact(
                                                    'projectManagerId',
                                                )}
                                            >
                                                Edit Project Manager Details
                                            </SmallButton>
                                        )}
                                    </FormGroup>
                                </FormGroupColumn>
                            </GroupedRow>
                        </>
                    )}
                    <GroupedRow>
                        {contract?.invoiceHasInspectorName && (
                            <FormGroupColumn>
                                <Input
                                    id="inspectorName"
                                    name="project.inspectorName"
                                    label="Inspector Name"
                                    value={project.inspectorName ?? ''}
                                    onChange={this.onChange}
                                />
                            </FormGroupColumn>
                        )}
                        {contract?.subcontractorsApply && (
                            <FormGroupColumn>
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="subcontractorId"
                                        text="Subcontractor"
                                        required={
                                            !!contract
                                                .subcontractorsApply
                                        }
                                    />
                                    <ValidatedSelect
                                        id="subcontractorId"
                                        name="project.subcontractorId"
                                        options={customerSubcontractors || []}
                                        value={
                                            (customerSubcontractors ?? []).find(
                                                (x) => x.value === project.subcontractorId
                                            ) ?? ''
                                        }
                                        onChange={this.onSelectChanged}
                                        required={contract?.subcontractorsApply}
                                        validationMessage="A subcontractor selection is required."
                                    />
                                </FormGroup>
                            </FormGroupColumn>
                        )}
                        {project?.jobTypeId !== 1 && (
                            <FormGroupColumn>
                                <FormGroup>
                                    <FormLabel
                                        htmlFor="jobType"       
                                        text="What type of work is being done?"
                                        required
                                    />
                                    <ValidatedSelect
                                        id="jobType"
                                        name="project.jobTypeId"
                                        options={jobTypesOptions}
                                        value={
                                            (jobTypesOptions ?? []).find(
                                                (x) => x.value === project.jobTypeId,
                                            ) ?? ''
                                        }
                                        onChange={this.onSelectChanged}
                                        required
                                        validationMessage="A job type selection is required."
                                    />
                                </FormGroup>
                            </FormGroupColumn>
                        )}
                    </GroupedRow>

                    <GroupedRow>
                        <FormGroupColumn>
                            <LocationSelector
                                label="Meeting Location"
                                type="meetingLocation"
                                stateData={project}
                                contract={contract}
                                usCounties={usCounties}
                                addressFormRef={this.addressFormRef}
                                setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                locationRequired={meetingLocationRequired}
                            />
                        </FormGroupColumn>
                        <FormGroupColumn>
                            <LocationSelector
                                label="Job Location"
                                type="jobLocation"
                                stateData={project}
                                contract={contract}
                                usCounties={usCounties}
                                addressFormRef={this.addressFormRef}
                                setSelectedAddressType={(type) => this.setState({ selectedAddressType: type })}
                                locationRequired={jobLocationRequired}
                            />
                        </FormGroupColumn>
                    </GroupedRow>
                    {/* Notes */}
                    <GroupedRow>
                        <Col>
                            <Textarea
                                name="project.notes"
                                label="Notes"
                                maxLength={1000}
                                value={project?.notes || ''}
                                onChange={this.onChange}
                            />
                        </Col>
                    </GroupedRow>

                    <div className="m-3">
                        <JobScheduler
                            onDateSelect={this.handleJobCreate}
                            project={project}
                            onJobSelect={(job, date) => this.handleJobSelect(job, date)}
                        />
                    </div>

                    {Boolean(contract.isProjectContract) && (
                        <div className="m-3">
                            <CollapseUnderlineHeader
                                headerText="Equipment on Site"
                                isOpen={this.state.equipmentOnSiteOpen}
                                toggleCollapse={() => this.setState({
                                    equipmentOnSiteOpen: !this.state.equipmentOnSiteOpen,
                                })}
                            >
                                <EquipmentOnSite resources={project.resources} />
                            </CollapseUnderlineHeader>
                        </div>
                    )}

                    <FlexCenterRow className="mb-3 mt-4">
                        <Button
                            type="submit"
                            color="primary"
                            name="projectForm"
                            disabled={saving}
                        >
                            {saving ? (
                                <div className="d-flex align-items-center justify-content-center">
                                    <Spinner
                                        size="sm"
                                        className="saving-button-progress text-success mr-2"
                                    />
                                    <span className="ml-2">Saving</span>
                                </div>
                            ) : (
                                <>
                                    <FontAwesomeIcon className="mr-2" icon={faSave} />
                                    {!existing ? 'Save New Project' : 'Save Project Details'}
                                </>
                            )}
                        </Button>

                        {project?.id && project?.jobs?.length > 0 && (
                            <Link
                                component={Button}
                                color="primary"
                                className="ml-2"
                                type="button"
                                to={dispatchPath}
                            >
                                Go to Dispatch Board
                            </Link>
                        )}
                    </FlexCenterRow>
                </AppPageForm>
                <JobForm
                    selectedDate={selectedDate}
                    contract={contract}
                    contractResources={contractResources}
                    project={project}
                    jobTypesOptions={jobTypesOptions}
                    slideOpen={slideOpen}
                    setSlideOpen={(open) => this.setState({ slideOpen: open })}
                    usCounties={usCounties}
                    contactsOptions={contactsOptions}
                    setContactsOptions={(contacts) =>
                        this.setState({ contactsOptions: contacts })
                    }
                    dispatchLocations={dispatchLocations}
                    customerSubcontractors={customerSubcontractors}
                    contactFormRef={this.contactFormRef}
                    selectedJob={selectedJob}
                    onAddContact={this.onAddContact}
                    onEditContact={this.onEditContact}
                    onJobSave={this.handleJobSave}
                    refreshProject={this.refreshProject}
                />
                
                {Boolean(usCounties) && (
                    <AddressFormNew
                        id="jobAddressForm"
                        ref={this.addressFormRef}
                        contractUsesPrevailingWage={contract?.prevailingWage}
                        isUnionWork={contract?.isUnionWork}
                        contractId={contract?.id}
                        countryId={contract?.countryId}
                        countryCode={contract?.countryCode}
                        onSaveCallback={this.onSaveAddress}
                        usCounties={usCounties}
                    />
                )}
                
                <OrganizationContactForm
                    ref={this.contactFormRef}
                    show={showContactsForm}
                    readOnly={contactFormIsReadOnly}
                    allowContactTypeEdit={allowContactTypeEdit}
                    contact={selectedContact}
                    onClose={() => this.setState({ showContactsForm: false })}
                    onChange={(e) => {
                        selectedContact[e.target.name] = e.target.value;
                        this.setState({
                            selectedContact: { ...selectedContact },
                        });
                    }}
                    onCheckedChanged={(e) => {
                        selectedContact[e.target.name] = e.target.checked;
                        this.setState({
                            selectedContact: { ...selectedContact },
                        });
                    }}
                    onContactTypeChanged={(items) => {
                        selectedContact.contactTypes = items.map(
                            (x) => x.value,
                        );
                        this.setState({
                            selectedContact: { ...selectedContact },
                        });
                    }}
                    onSaveCallback={this.onOrganizationContactSaved}
                />
            </>
        );
    }
}

export default withRouter(ProjectForm);
